import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Popover, PopoverContent, PopoverTrigger, Stack, Text } from '@nike/podium';
import { Badge } from '~/components/shadcn/badge';
import { IconActionButton, IconCloseButton } from '~/components/buttons';
import { normalizeWidthAbbreviation, getWidthLabel, getIconForWidth, getGenderCategory } from '~/utils/genderFitUtils';
function formatIconLabel(src, fallback) {
    const filename = src.split('/').pop()?.replace('.svg', '') ?? fallback;
    return filename.replace('Icon', '').replace('Shoe', '');
}
function DashPlaceholder() {
    return (_jsx(Box, { className: "flex items-center justify-center", style: { width: '32px', height: '32px' }, children: _jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "-" }) }));
}
function IconBox({ src, alt, models = [], project }) {
    const label = formatIconLabel(src, alt);
    const [isOpen, setIsOpen] = useState(false);
    const closeTimeoutRef = useRef(null);
    const navigate = useNavigate();
    const handleModelClick = (modelId) => {
        navigate(`/models/detail/${modelId}`, {
            state: {
                from: '/projects/view/card',
                fromLabel: 'Back to Projects',
                projectId: project?.id,
                returnToProjectPanel: true
            }
        });
    };
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);
    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsOpen(true);
    };
    const handleMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 150);
    };
    const iconElement = (_jsx(Box, { className: "w-[38px] h-[38px] flex items-center justify-center cursor-pointer border border-transparent hover:border-border rounded", children: _jsx("img", { src: src, alt: alt, className: "w-8 h-8" }) }));
    if (models.length === 0) {
        return iconElement;
    }
    return (_jsxs(Popover, { open: isOpen, onOpenChange: setIsOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx("div", { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: iconElement }) }), _jsx(PopoverContent, { side: "bottom", align: "start", sideOffset: 8, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: _jsxs(Stack, { gap: "m", children: [_jsxs(Text, { font: "body-1-strong", children: [label, " Models"] }), _jsx(Stack, { gap: "s", children: models.map((model, index) => (_jsx(Box, { onClick: () => handleModelClick(model.id), className: "group/row rounded border-2 border-transparent hover:border-border p-1 -m-1 cursor-pointer", children: _jsxs(Stack, { horizontal: true, justify: "between", align: "start", children: [_jsxs(Stack, { gap: "xs", children: [_jsx(Text, { font: "body-1-strong", children: model.name }), _jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: model.modelNumber })] }), _jsxs(Stack, { horizontal: true, gap: "s", align: "center", children: [_jsx(Badge, { variant: "secondary", className: "pds-color-text-secondary rounded px-3 py-1.5 bg-[#9E9EA0]/20 text-xs", children: model.widthLabel }), _jsx(IconCloseButton, { onClick: () => { }, hoverGroupClass: "group-hover/row" })] })] }) }, index))) })] }) })] }));
}
function GenderRow({ label, icons, project }) {
    return (_jsxs(Stack, { horizontal: true, gap: "s", className: "w-full group", align: "start", children: [_jsx(Box, { width: 80, className: "pt-1", children: _jsx(Badge, { variant: "secondary", className: "pds-color-text-secondary px-3 py-1.5 bg-[#9E9EA0]/20 hover:bg-[#9E9EA0]/30 text-xs", style: {
                        borderRadius: '4px'
                    }, children: label }) }), icons.map((icon, index) => (_jsx(Box, { className: "flex-1 flex justify-center", children: icon === 'dash' ? (_jsx(DashPlaceholder, {})) : (_jsx(IconBox, { src: icon.src, alt: icon.alt, models: icon.models, project: project })) }, index))), _jsx(Box, { className: "flex-1 flex justify-center", children: _jsx(IconActionButton, {}) })] }));
}
export function GenderFitVariations({ project }) {
    const genderRows = useMemo(() => {
        const models = project?.models ?? [];
        const grouped = {
            men: {},
            women: {},
            unisex: {},
            kids: {}
        };
        models.forEach((model) => {
            const gender = getGenderCategory(model);
            const rawWidth = model.styleDimensionAbbreviation ?? 'Medium';
            const normalizedWidth = normalizeWidthAbbreviation(rawWidth);
            if (!grouped[gender][normalizedWidth]) {
                grouped[gender][normalizedWidth] = [];
            }
            grouped[gender][normalizedWidth].push({
                id: model.modelId.toString(),
                name: model.modelName,
                widthLabel: getWidthLabel(rawWidth),
                modelNumber: model.modelId.toString()
            });
        });
        const STANDARD_WIDTHS = ['MEDIUM', '2E', 'NARROW'];
        const buildIconSlots = (genderKey) => {
            const widths = grouped[genderKey];
            return STANDARD_WIDTHS.map((widthAbbr) => {
                // Always show dash for all widths in unisex and kids
                if (genderKey === 'unisex' || genderKey === 'kids') {
                    return 'dash';
                }
                // Always show dash for narrow width in men and women
                if ((genderKey === 'men' || genderKey === 'women') &&
                    widthAbbr === 'NARROW') {
                    return 'dash';
                }
                const models = widths[widthAbbr];
                if (!models || models.length === 0) {
                    return 'dash';
                }
                const iconConfig = getIconForWidth(widthAbbr);
                return {
                    ...iconConfig,
                    models
                };
            });
        };
        return {
            men: buildIconSlots('men'),
            women: buildIconSlots('women'),
            unisex: buildIconSlots('unisex'),
            kids: buildIconSlots('kids')
        };
    }, [project]);
    const hasModels = project?.models && project.models.length > 0;
    if (!hasModels) {
        return (_jsx(Text, { font: "body-2", className: "pds-color-text-secondary", children: "No model variations available" }));
    }
    return (_jsxs(Stack, { gap: "m", children: [_jsx(GenderRow, { label: "Men", icons: genderRows.men, project: project }), _jsx(GenderRow, { label: "Women", icons: genderRows.women, project: project }), _jsx(GenderRow, { label: "Unisex", icons: genderRows.unisex, project: project }), _jsx(GenderRow, { label: "Kids", icons: genderRows.kids, project: project })] }));
}
